import type { InstrumentTickPayload } from '@/lib/instrumentTick';

// ===== Tuning =====
const TICK_MS = 1000;
const SEED_COUNT = 200; // number of ticks in the seed burst

// Seed burst — gap between successive seed ticks (ms)
const SEED_TICK_SPACING_MS = 1000;

// Momentum / random walk
const FLAT_TICK_CHANCE = 0.4; // probability of no change per tick → flat stretches
const VELOCITY_RANDOM_SCALE = 0.8; // amplitude of random impulse added to velocity
const VELOCITY_DECAY = 0.92; // multiplicative damping each tick (lower = damped faster)

// Default instrument when none is provided
const DEFAULT_INSTRUMENT_CODE = 'BTC';
// =================

/**
 * Simulates the backend `instrument:price` Socket.IO feed for local dev.
 *
 * Emits payloads in the exact production wire shape ({@link InstrumentTickPayload})
 * so the same parser ({@link parseInstrumentTick}) handles mock + live, and
 * `MockPriceSocket` stays a faithful stand-in for the real socket.
 *
 * Uses momentum-based movement so the price flows in smooth curves instead of
 * jittering randomly. On `subscribe` it emits a seed burst, then continues at
 * ~5 Hz.
 */
export class MockPriceSocket {
  private lastPrice: number;
  private basePrice: number;
  private instrumentCode: string;
  private symbol: string;
  private velocity = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private callback: ((payload: InstrumentTickPayload) => void) | null = null;

  constructor(basePrice: number, instrumentCode: string = DEFAULT_INSTRUMENT_CODE) {
    this.basePrice = basePrice;
    this.lastPrice = basePrice;
    this.instrumentCode = instrumentCode;
    this.symbol = `FS:${instrumentCode}USD:BLD`;
  }

  /** Advance price by one momentum step. */
  private step(scale: number) {
    if (scale === 1 && Math.random() < FLAT_TICK_CHANCE) return;

    this.velocity += (Math.random() - 0.5) * VELOCITY_RANDOM_SCALE * scale;
    this.velocity *= VELOCITY_DECAY;
    this.lastPrice += this.velocity;
  }

  /** Build a production-shaped payload for the current `lastPrice` at `timeMs`. */
  private buildPayload(timeMs: number): InstrumentTickPayload {
    return {
      event: 'prediction:market:event',
      type: 'instrument_updated',
      instrumentCode: this.instrumentCode,
      symbol: this.symbol,
      sendingTime: new Date(timeMs).toISOString(),
      lastPrice: this.lastPrice,
    };
  }

  /** Begin emitting wire-shaped price ticks to `cb`. */
  subscribe(cb: (payload: InstrumentTickPayload) => void) {
    this.callback = cb;
    this.lastPrice = this.basePrice;
    this.velocity = 0;

    // Seed burst — ticks spaced `SEED_TICK_SPACING_MS` apart, ending at "now"
    const nowMs = Date.now();
    const seedStartMs = nowMs - (SEED_COUNT - 1) * SEED_TICK_SPACING_MS;

    for (let i = 0; i < SEED_COUNT; i++) {
      this.step(1);
      cb(this.buildPayload(seedStartMs + i * SEED_TICK_SPACING_MS));
    }

    // Ongoing ticks — `sendingTime` carries ms precision so each tick is unique
    this.intervalId = setInterval(() => {
      if (!this.callback) return;
      this.step(1);
      this.callback(this.buildPayload(Date.now()));
    }, TICK_MS);
  }

  /** Stop emitting and clean up. */
  unsubscribe() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.callback = null;
  }
}
